import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface SandboxProps {
  /**
   * Optional callback that returns an object representing the current selection.
   * Only one selection is allowed overall.
   * {
   *   L0: string | null, // Selected basic tile from L0 (or null if nothing is selected)
   *   L1: string | null, // Selected tile from L1 (or null)
   *   L3: boolean        // Whether the L3 option (color) is selected (true) or not (false)
   * }
   */
  onTileSelect?: (selection: { L0: string | null; L1: string | null; L3: boolean }) => void;
}

// Define the common hexagon clip-path
const hexClipPath =
  "polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)";

// Size constants for the hex tiles (smaller)
const HEX_SIZE = 60; // width in pixels
const HEX_HEIGHT = 52; // approximate height for a hex shape

// Define tile groups as arrays (for selection UI)
// We insert "erase" as the first option for L0 and L1.
const tileGroups = {
  L0: ["erase", "grass", "forest", "mountain", "swamp", "water", "cloud", "hills", "impassible_mountain"],
  L1: ["erase", "dwarvern_cave", "elven_tree", "halfling_capitol", "halfling_village", "human_village", "lighthouse", "boats"],
  // L2 is intentionally left out.
};

/*
  For L0 and L1 we store the currently selected tile as a string or null (if nothing is selected).  
  For L3 we change the state from a boolean to a tri‑state value:
    - "color"  means the L3 color option is active.
    - "erase"  means the L3 erase option is active.
    - null     means nothing is selected.
  In the onTileSelect callback we map L3 to a boolean: 
    true if "color" is selected, false otherwise.
*/
const Sandbox: React.FC<SandboxProps> = ({ onTileSelect }) => {
  const theme = useTheme();

  const [selectedL0, setSelectedL0] = useState<string | null>(null);
  const [selectedL1, setSelectedL1] = useState<string | null>(null);
  const [selectedL3, setSelectedL3] = useState<"color" | "erase" | null>(null);

  // Helper to call the callback (if provided) with the current selection.
  // For L0 and L1, we send null if no tile is selected.
  // For L3 we send true only if "color" is active.
  const updateSelection = (newSelections: {
    L0?: string | null;
    L1?: string | null;
    L3?: boolean;
  }) => {
    const selection = {
      L0: newSelections.L0 !== undefined ? newSelections.L0 : selectedL0,
      L1: newSelections.L1 !== undefined ? newSelections.L1 : selectedL1,
      L3:
        newSelections.L3 !== undefined
          ? newSelections.L3
          : selectedL3 === "color" // true only if "color" is active
            ? true
            : false,
    };
    if (onTileSelect) {
      onTileSelect(selection);
    }
  };

  // --- L0 Handlers ---

  const handleL0Click = (tile: string) => {
    // If the clicked tile is already selected, toggle it off.
    if (selectedL0 === tile) {
      setSelectedL0(null);
      updateSelection({ L0: null });
    } else {
      setSelectedL0(tile);
      // Also clear selections for L1 and L3.
      setSelectedL1(null);
      setSelectedL3(null);
      updateSelection({ L0: tile, L1: null, L3: false });
    }
  };

  // --- L1 Handlers ---

  const handleL1Click = (tile: string) => {
    if (selectedL1 === tile) {
      setSelectedL1(null);
      updateSelection({ L1: null });
    } else {
      setSelectedL1(tile);
      setSelectedL0(null);
      setSelectedL3(null);
      updateSelection({ L0: null, L1: tile, L3: false });
    }
  };

  // --- L3 Handlers ---

  // For the L3 color option.
  const handleL3Click = () => {
    if (selectedL3 === "color") {
      // Toggle off if already selected.
      setSelectedL3(null);
      updateSelection({ L3: false });
    } else {
      setSelectedL3("color");
      // Clear other selections.
      setSelectedL0(null);
      setSelectedL1(null);
      updateSelection({ L0: null, L1: null, L3: true });
    }
  };

  // For the L3 erase option.
  const handleL3EraseClick = () => {
    if (selectedL3 === "erase") {
      // Toggle off.
      setSelectedL3(null);
      updateSelection({ L3: false });
    } else {
      setSelectedL3("erase");
      setSelectedL0(null);
      setSelectedL1(null);
      updateSelection({ L0: null, L1: null, L3: false });
    }
  };

  // A reusable component for rendering a hex tile.
  const renderHexTile = (
    tile: string,
    isSelected: boolean,
    onClick: () => void,
    key: string
  ) => {
    // For the "erase" option in L0 and L1, we consider it active only if the state exactly equals "erase".
    const active =
      tile === "erase"
        ? key.startsWith("L0")
          ? selectedL0 === "erase"
          : selectedL1 === "erase"
        : isSelected;
    const borderColor = active ? theme.palette.primary.main : "#ffffff";
    const boxShadow = active
      ? `0 0 10px 3px ${theme.palette.primary.main}`
      : `0 0 5px 1px #ffffff`;

    return (
      <Grid item xs={6} key={key} sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          onClick={onClick}
          sx={{
            width: HEX_SIZE,
            height: HEX_HEIGHT,
            clipPath: hexClipPath,
            backgroundColor: borderColor,
            boxShadow: boxShadow,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out",
          }}
        >
          <Box
            sx={{
              width: "85%",
              height: "85%",
              clipPath: hexClipPath,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* For the erase option, we can simply display text */}
            {tile === "erase" ? (
              <Typography variant="caption" color={active ? "#ffffff" : "#000000"}>
                Erase
              </Typography>
            ) : (
              <img
                src={`/tile_types/${tile}.png`}
                alt={tile}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowY: "auto",
        padding: 1,
        // Hide scrollbar on WebKit browsers
        "&::-webkit-scrollbar": { display: "none" },
        // Hide scrollbar on Firefox and IE/Edge
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {/* L0 Section: Basic Tiles */}
      <Typography variant="subtitle1" align="center" sx={{ mt: 2, mb: 1 }}>
        L0 - Basic Tiles
      </Typography>
      <Grid container spacing={1} justifyContent="center">
        {tileGroups.L0.map((tile) =>
          renderHexTile(
            tile,
            // For the "erase" option, active if selectedL0 equals "erase"
            tile === "erase" ? selectedL0 === "erase" : selectedL0 === tile,
            () => handleL0Click(tile),
            `L0-${tile}`
          )
        )}
      </Grid>

      {/* L1 Section: Villages / Structures */}
      <Typography variant="subtitle1" align="center" sx={{ mt: 2, mb: 1 }}>
        L1 - Villages / Structures
      </Typography>
      <Grid container spacing={1} justifyContent="center">
        {tileGroups.L1.map((tile) =>
          renderHexTile(
            tile,
            tile === "erase" ? selectedL1 === "erase" : selectedL1 === tile,
            () => handleL1Click(tile),
            `L1-${tile}`
          )
        )}
      </Grid>
    </Box>
  );
};

export default Sandbox;